import { Button } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useEffect, useId, useRef } from 'react'
import { useVocalCopy } from '../pitch-training/use-vocal-copy.js'
import { runSummaryHeadline, type RunKind, type RunOutcome } from './section-run.js'
import styles from './phrase-run-summary.module.css'

/**
 * The end of "Sing this section" (Pitch Trainer Phase 2, plan M6 step 6.7; Joni §6): the strict count, a row
 * per phrase with its headline verdict and `Sing again`, then the section again or Done. The headline takes
 * focus, as every new screen's does.
 */

export interface PhraseRunSummaryProps {
  kind: RunKind
  /** One per step, in run order; no outcome when the step was never sung whole */
  rows: { label: string; outcome: RunOutcome | undefined }[]
  /** Runs that step alone, then comes back here */
  onSingAgain: (index: number) => void
  onSingSectionAgain: () => void
  onDone: () => void
}

/** The verdict's lexicon name ("Started on pitch, sagged"), short enough for a row */
const VerdictName: FC<{ lexiconId: string }> = ({ lexiconId }) => {
  const copy = useVocalCopy(lexiconId)
  return copy ? <>{copy.displayName}</> : null
}

const Outcome: FC<{ outcome: RunOutcome | undefined }> = ({ outcome }) => {
  if (!outcome) {
    return <>Not sung</>
  }
  if (outcome.kind === 'not-heard') {
    return <VerdictName lexiconId={outcome.lexiconId} />
  }
  return outcome.headline ? <VerdictName lexiconId={outcome.headline.id} /> : <>{`${outcome.scorePercent}%`}</>
}

export const PhraseRunSummary: FC<PhraseRunSummaryProps> = props => {
  const { kind, rows, onSingAgain, onSingSectionAgain, onDone } = props
  const headingRef = useRef<HTMLHeadingElement>(null)
  const rowId = useId()

  useEffect(() => {
    headingRef.current?.focus()
  }, [])

  return (
    <section className={styles.summary} aria-label="Summary">
      <h2 ref={headingRef} tabIndex={-1} className={styles.title}>
        {runSummaryHeadline(
          kind,
          rows.map(row => row.outcome)
        )}
      </h2>
      <ol className={styles.rows}>
        {rows.map((row, index) => (
          <li key={row.label} className={styles.row}>
            <span className={styles.text}>
              <span id={`${rowId}-${index}`} className={styles.label}>
                {row.label}
              </span>
              <span className={styles.verdict}>
                <Outcome outcome={row.outcome} />
              </span>
            </span>
            <Button
              variant="outlined"
              size="small"
              aria-describedby={`${rowId}-${index}`}
              onClick={() => onSingAgain(index)}
            >
              Sing again
            </Button>
          </li>
        ))}
      </ol>
      <div className={styles.actions}>
        <Button variant="contained" onClick={onSingSectionAgain}>
          Sing the section again
        </Button>
        <Button variant="text" onClick={onDone}>
          Done
        </Button>
      </div>
    </section>
  )
}
